import { ChangeDetectionStrategy, Component, Input, OnInit } from "@angular/core";
import { CountersignRole, PersonRole } from "@ha/data/basic";
import { HaModalService } from "@ha/ui/common";
import { Select, Store } from "@ngxs/store";
import { Observable } from "rxjs";

import { PermitTeam } from "../../../permit-team/models/permit-team.model";
import { PermitTeamActions } from "../../../permit-team/states/permit-team.action";
import { PermitTeamSelectors } from "../../../permit-team/states/permit-team.selectors";
import { PermitStepActions } from "../../../permit/states/permit-step.action";
import { PermitStepSelectors } from "../../../permit/states/permit-step.selectors";
import { PermitCreateSignPersonModalComponent, PermitCreateSignPersonModalComponentData } from "../../../shared/components/permit-create-sign-person-modal/permit-create-sign-person-modal.component";
import { ViewSignatureModalComponent, ViewSignatureModalComponentData } from "../../../shared/components/view-signature-modal/view-signature-modal.component";
import { PermitPerson } from "../../../shared/models/permit-person.model";
import { PermitCreateSelectors } from "../../states/permit-create.selectors";
import { WorkplaceDetails } from "../../models/workplace-details.model";
import { AddCountersignModalComponent } from "../../../permit-team/components/add-countersign-modal/add-countersign-modal.component";
import { PermitTeamTextMember } from "../../../permit-team/models/permit-team-commands.model";
import { RemovePersonModalComponent, RemovePersonModalComponentData } from "../../../permit-team/components/remove-person-modal/remove-person-modal.component";
import { CountersignPermit } from "../../../shared/models/countersign-permit.model";

@Component({
    selector: "pmt-signature",
    templateUrl: "permit-signature.component.html",
    styleUrls: ["permit-signature.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PermitSignatureComponent implements OnInit {
    public PersonRole = PersonRole;

    @Input()
    public permitId : number | undefined;

    @Select(PermitCreateSelectors.isSuperintendentFlow)
    public isSuperintendentFlow$: Observable<boolean | undefined>;

    @Select(PermitCreateSelectors.isFlammableHotWork)
    public isFlammableHotWork$: Observable<boolean>;

    @Select(PermitTeamSelectors.team)
    public team$: Observable<PermitTeam>;

    @Select(PermitTeamSelectors.teamSigned)
    public teamSigned$: Observable<boolean>;

    @Select(PermitCreateSelectors.workplaceDetails)
    public workplaceDetails$: Observable<WorkplaceDetails>;

    public CountersignRole = CountersignRole;

    public roleLabels: Record<number, string> = {
        [CountersignRole.FacilityManager]: "countersignroles.facilitymanager",
        [CountersignRole.WorkEnvironmentManager]: "countersignroles.workenvironmentmanager",
        [CountersignRole.BAS_U]: "countersignroles.BAS_U",
        [CountersignRole.Client]: "countersignroles.client",
        [CountersignRole.FireSafetyManager]: "countersignroles.firesafetymanager",
        [CountersignRole.FireSafetyCoordinator]: "countersignroles.firesafetycoordinator",
        [CountersignRole.OperationsManager]: "countersignroles.operationsmanager",
        [CountersignRole.PropertyOwner]: "countersignroles.propertyowner",
        [CountersignRole.SecurityManager]: "countersignroles.securitymanager",
        [CountersignRole.OtherRole]: "countersignroles.otherrole"
    };

    public countersigns$: Observable<PermitTeamTextMember[]>;

    public roleLangKeys: Record<number, string> = {};

    constructor(
            private store: Store,
            private modalService: HaModalService,
        ) {
        }


    public get isDemo(): boolean {
        return this.store.selectSnapshot(PermitCreateSelectors.isDemo);
    }

    public get isSuperintendentFlow(): boolean | undefined {
        return this.store.selectSnapshot(PermitCreateSelectors.isSuperintendentFlow);
    }

    public ngOnInit(): void {
        this.store.dispatch(new PermitTeamActions.GetTeam(this.store.selectSnapshot(PermitStepSelectors.permitId)));
    }

    private getPersonRoles(teamMember: PermitPerson): PersonRole[] {
        const team = this.store.selectSnapshot(PermitTeamSelectors.team);
        const roles: PersonRole[] = [];

        if (team.superintendent?.personId === teamMember.personId) {
            roles.push(PersonRole.Superintendent);
        }
        if (team.permitIssuer?.personId === teamMember.personId) {
            roles.push(PersonRole.PermitIssuer);
        }
        if (team.hotWorkers?.some((hw) => hw.personId === teamMember.personId)) {
            roles.push(PersonRole.HotWorker);
        }
        if (team.fireWatchers?.some((fw) => fw.personId === teamMember.personId)) {
            roles.push(PersonRole.FireWatcher);
        }
        if (team.postFireWatchers?.some((pfw) => pfw.personId === teamMember.personId)) {
            roles.push(PersonRole.PostFireWatcher);
        }

        return roles;
    }

    public isPermitIssuer(teamMember: PermitPerson): boolean {
        const team = this.store.selectSnapshot(PermitTeamSelectors.team);
        return team.permitIssuer?.personId === teamMember.personId;
    }

    public addCountersign() {
        this.modalService.open(AddCountersignModalComponent,  true, { "permitId" : this.permitId });
    }

    public removeCountersign(countersign: CountersignPermit) {
         this.modalService.open(RemovePersonModalComponent, false, <RemovePersonModalComponentData>{
            permitId: this.permitId,
            role: null,
            person: {
                personId: 0,
                firstName: countersign.member,
                lastName: "",
                textMemberId: countersign.memberId,
                isTextMember: true
            } as unknown as PermitPerson
        });
    }

    public signatureNotRequired(teamMember: PermitPerson): boolean {
        if (!this.isDemo) {
            return false;
        }

        if (this.isSuperintendentFlow === true) {
            return false;
        }

        return !this.isPermitIssuer(teamMember);
    }

    public showSignPerson(teamMember: PermitPerson) {
        if (this.signatureNotRequired(teamMember)) {
            return;
        }

        const roles = this.getPersonRoles(teamMember);

        if (!teamMember.signature) {
            this.modalService.open(
                PermitCreateSignPersonModalComponent,
                true,
                <PermitCreateSignPersonModalComponentData> {
                    person: teamMember,
                    permitId: this.store.selectSnapshot(PermitStepSelectors.permitId),
                    roles: roles,
                    isDemo: this.isDemo,
                },
            );
        } else {
            this.modalService.open(
                ViewSignatureModalComponent,
                true,
                <ViewSignatureModalComponentData> {
                    name: `${teamMember.firstName} ${teamMember.lastName}`,
                    signature: teamMember.signature,
                },
            );
        }
    }

    public continue(): void {
        if (!this.store.selectSnapshot(PermitTeamSelectors.teamSigned)) {
            return;
        }

        this.store.dispatch(new PermitStepActions.GoForward());
    }

    public back(): void {
        this.store.dispatch(new PermitStepActions.GoBackwards(this.store.selectSnapshot(PermitStepSelectors.permitId)));
    }
}
